import React, { useState, useEffect } from 'react';
import { useCopilot } from '../contexts/CopilotContext';
import { getCategoryColor } from '../theme';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Skeleton,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CalendarToday,
  Warning,
  CheckCircle,
  Error,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Notifications,
  Schedule,
  AccessTime,
  Email as EmailIcon,
  History as HistoryIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import ReminderEditDialog from './ReminderEditDialog';
import EmailDialog from './EmailDialog';
import config from '../config';

interface Certificate {
  id: string;
  certificate_type: string;
  certificate_type_category?: string;
  issue_date: string;
  expiry_date: string;
  property_address: string;
  street_address?: string;
  contractor_name?: string;
  original_filename?: string;
  pdf_filename?: string;
  normalized_address?: string;
  inspector_name?: string;
  certificate_number?: string;
  notes?: string;
  created_at?: string;
  last_email_sent_at?: string;
  last_email_sent_to?: string;
}

interface ReminderStatus {
  has_reminders: boolean;
  total_active_reminders: number;
  active_reminders: Array<{
    id: string;
    reminder_days_before: number;
    reminder_date: string;
    status: string;
  }>;
  reminders?: Array<{
    id: string;
    reminder_days_before: number;
    reminder_date: string;
    status: string;
  }>;
}

interface EnhancedCertificateCardProps {
  certificate: Certificate;
  isSelected?: boolean;
  onSelectionChange?: (certificateId: string, selected: boolean) => void;
  onEdit?: (certificate: Certificate) => void;
  onDelete?: (certificateId: string) => void;
  onView?: (certificateId: string) => void;
  showSelection?: boolean;
  onReminderSuccess?: (message: string) => void;
  onReminderError?: (error: string) => void;
  reminderRefreshTrigger?: number;
  isNewlyAdded?: boolean;
  isAutoExpanded?: boolean;
  isDimmed?: boolean;
  hideActions?: boolean; // hides version history, email and reminder buttons (read-only display)
}

const EnhancedCertificateCard: React.FC<EnhancedCertificateCardProps> = ({
  certificate,
  isSelected = false,
  onSelectionChange,
  onEdit,
  onDelete,
  onView,
  showSelection = true,
  onReminderSuccess,
  onReminderError,
  reminderRefreshTrigger = 0,
  isNewlyAdded = false,
  isAutoExpanded = false,
  isDimmed = false,
  hideActions = false
}) => {
  const { showMessage } = useCopilot();
  const [reminderStatus, setReminderStatus] = useState<ReminderStatus | null>(null);
  const [reminderMenuAnchor, setReminderMenuAnchor] = useState<null | HTMLElement>(null);
  const [isCreatingReminder, setIsCreatingReminder] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);

  // Log when isNewlyAdded changes
  useEffect(() => {
    if (isNewlyAdded) {
      console.log(`✨ Certificate ${certificate.id} is newly added - should be pulsing!`);
    }
  }, [isNewlyAdded, certificate.id]);
  
  // Check certificate status
  const getCertificateStatus = () => {
    const today = new Date();
    const expiryDate = new Date(certificate.expiry_date);
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'expired';
    if (diffDays <= 30) return 'expiring';
    return 'valid';
  };
  
  const certStatus = getCertificateStatus();
  
  // Check if should show highlight
  const showGreenHighlight = isNewlyAdded && !hasBeenClicked;
  const showRedHighlight = isAutoExpanded && !hasBeenClicked && certStatus === 'expired';
  const showYellowHighlight = isAutoExpanded && !hasBeenClicked && certStatus === 'expiring';

  // Reset hasBeenClicked whenever isAutoExpanded turns on so re-clicking the filter re-pulses
  useEffect(() => {
    if (isAutoExpanded) setHasBeenClicked(false);
  }, [isAutoExpanded]);

  // Auto-remove highlights after 5 seconds
  useEffect(() => {
    if (showGreenHighlight || showRedHighlight || showYellowHighlight) {
      const timer = setTimeout(() => {
        setHasBeenClicked(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showGreenHighlight, showRedHighlight, showYellowHighlight]);

  useEffect(() => {
    loadReminderStatus();
  }, [certificate.id, reminderRefreshTrigger]);

  const loadReminderStatus = async () => {
    setLoadingReminders(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${config.apiUrl}/api/reminders/certificate/${certificate.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`🔍 Reminder status for certificate ${certificate.id}:`, data.reminder_status);
        setReminderStatus(data.reminder_status);
      }
    } catch (err) {
      console.error('Error loading reminder status:', err);
    } finally {
      setLoadingReminders(false);
    }
  };

  const createReminder = async (days: number) => {
    setIsCreatingReminder(true);
    setReminderMenuAnchor(null);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        onReminderError?.('Please log in first');
        return;
      }

      const response = await fetch(`${config.apiUrl}/api/reminders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          certificate_id: certificate.id,
          reminder_days: days
        })
      });

      const data = await response.json();

      if (data.success) {
        await loadReminderStatus();
        onReminderSuccess?.(`✅ Reminder created: ${days} days before expiry`);
      } else {
        onReminderError?.(data.error || 'Failed to create reminder');
        return;
      }
    } catch (err) {
      const errorMessage = (err as Error).message || 'Failed to create reminder';
      onReminderError?.(errorMessage);
    } finally {
      setIsCreatingReminder(false);
    }
  };

  const getDaysUntilExpiry = () => {
    try {
      // Check for missing/invalid date strings
      if (!certificate.expiry_date || 
          certificate.expiry_date === 'N/A' || 
          certificate.expiry_date.toLowerCase().includes('not_found') ||
          certificate.expiry_date.toLowerCase().includes('expiry_date_not_found')) {
        return null;
      }
      
      const expiryDate = new Date(certificate.expiry_date);
      
      // Check if date is valid
      if (isNaN(expiryDate.getTime())) {
        return null;
      }
      
      const today = new Date();
      const diffTime = expiryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  const getExpiryStatus = () => {
    const daysUntilExpiry = getDaysUntilExpiry();
    if (daysUntilExpiry === null) return { color: 'warning', label: 'No Date Found', severity: 'warning' };
    
    if (daysUntilExpiry < 0) {
      return { color: 'error', label: `Expired ${Math.abs(daysUntilExpiry)} days ago`, severity: 'error' };
    } else if (daysUntilExpiry === 0) {
      return { color: 'error', label: 'Expires today', severity: 'error' };
    } else if (daysUntilExpiry <= 30) {
      return { color: 'warning', label: `${daysUntilExpiry} days left`, severity: 'warning' };
    } else if (daysUntilExpiry <= 90) {
      return { color: 'info', label: `${daysUntilExpiry} days left`, severity: 'info' };
    } else {
      return { color: 'success', label: `${daysUntilExpiry} days left`, severity: 'success' };
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const expiryStatus = getExpiryStatus();
  const hasActiveReminders = reminderStatus?.has_reminders && 
    (reminderStatus.active_reminders?.length > 0 || reminderStatus.reminders?.some(r => r.status === 'active'));

  const handleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectionChange?.(certificate.id, event.target.checked);
  };

  const handleReminderClick = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    await loadReminderStatus(); // Ensure latest reminder data before opening dialog
    setReminderDialogOpen(true);
  };

  const handleReminderSave = async (reminderText: string, daysBeforeExpiry: number, reminderTime: string) => {
    try {
      setIsCreatingReminder(true);
      
      const token = localStorage.getItem('auth_token');
      const existingReminder = reminderStatus?.active_reminders?.[0];

      let response: Response;

      if (existingReminder) {
        // Edit the existing single reminder instead of creating a new one
        const expiryDate = new Date(certificate.expiry_date);
        expiryDate.setDate(expiryDate.getDate() - daysBeforeExpiry);
        const reminderDate = expiryDate.toISOString().split('T')[0];

        response = await fetch(`${config.apiUrl}/api/reminders/${existingReminder.id}/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            reminder_date: reminderDate,
            reminder_days_before: daysBeforeExpiry,
            reminder_time: reminderTime
          }),
        });
      } else {
        // No existing reminder — create one
        response = await fetch(`${config.apiUrl}/api/reminders/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            certificate_id: certificate.id,
            reminder_days: daysBeforeExpiry,
            reminder_time: reminderTime
          }),
        });
      }

      const result = await response.json();
      
      if (response.ok && result.success) {
        console.log('✅ Reminder saved successfully:', result);
        await loadReminderStatus();
        onReminderSuccess?.(`Reminder set for ${daysBeforeExpiry} ${daysBeforeExpiry === 1 ? 'day' : 'days'} before expiry`);
      } else {
        console.error('❌ Failed to save reminder:', response.status, result);
        
        let errorMessage = 'Failed to save reminder. Please try again.';
        if (result.code === 'AUTH_REQUIRED') {
          errorMessage = result.error || 'Failed to save reminder. Please try again.';
        } else if (result.code === 'CERT_EXPIRED') {
          errorMessage = 'The certificate has already expired. Reminders can only be set for active certificates.';
        } else if (result.code === 'DUPLICATE_REMINDER') {
          errorMessage = `A reminder for ${daysBeforeExpiry} days before expiry already exists for this certificate.`;
        } else if (result.code === 'INVALID_DATE') {
          errorMessage = 'The certificate has an invalid expiry date format.';
        } else if (result.error) {
          errorMessage = result.error;
        }
        
        throw new window.Error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving reminder:', error);
      const errorMessage = (error as Error)?.message || 'Failed to save reminder. Please try again.';
      onReminderError?.(errorMessage);
    } finally {
      setIsCreatingReminder(false);
    }
  };

  const handleReminderDialogClose = () => {
    setReminderDialogOpen(false);
  };

  const handleDeleteReminder = () => {
    const firstReminder = reminderStatus?.active_reminders?.[0];
    if (!firstReminder) {
      showMessage({ text: 'No active reminder found to delete.' });
      return;
    }
    showMessage({
      text: 'Delete this reminder? This will also remove the Google Calendar event.',
      confirm: {
        label: 'Yes, delete',
        onConfirm: async () => {
          try {
            const token = localStorage.getItem('auth_token');
            if (!token) { showMessage({ text: 'Authentication required.' }); return; }
            const response = await fetch(`${config.apiUrl}/api/reminders/${firstReminder.id}/delete`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            const result = await response.json().catch(() => ({ success: false, error: 'Invalid response' }));
            if (response.ok && result.success) {
              await loadReminderStatus();
              setReminderDialogOpen(false);
              onReminderSuccess?.('Reminder deleted successfully');
            } else {
              showMessage({ text: result.error || result.message || 'Failed to delete reminder.' });
            }
          } catch (error) {
            showMessage({ text: (error as Error)?.message || 'Failed to delete reminder. Please try again.' });
          }
        },
      },
    });
  };

  const reminderOptions = [
    { days: 30, label: '30 days before', icon: <Schedule /> },
    { days: 14, label: '14 days before', icon: <CalendarToday /> },
    { days: 7, label: '7 days before', icon: <AccessTime /> },
    { days: 3, label: '3 days before', icon: <Warning /> },
    { days: 1, label: '1 day before', icon: <Error /> }
  ];

  const handleOpenVersionHistory = async () => {
    setVersionDialogOpen(true);
    setLoadingVersions(true);
    setVersionError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificate.id}/versions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new window.Error('Failed to load version history');
      }

      const data = await response.json();
      setVersions(data.versions || []);
    } catch (error: any) {
      setVersionError(error.message || 'Failed to load versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRevertToVersion = (versionId: string) => {
    showMessage({
      text: 'Revert to this version? The current certificate data will be replaced.',
      confirm: {
        label: 'Yes, revert',
        onConfirm: async () => {
          try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${config.apiUrl}/api/certificates/${certificate.id}/revert/${versionId}`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new window.Error('Failed to revert to version');
            onReminderSuccess?.('Certificate reverted successfully');
            setVersionDialogOpen(false);
            window.location.reload();
          } catch (error: any) {
            showMessage({ text: error.message || 'Failed to revert version. Please try again.' });
          }
        },
      },
    });
  };

  return (
    <Card
      onClick={() => showGreenHighlight && setHasBeenClicked(true)}
      sx={{
        mb: 2,
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isSelected ? 'primary.50' : 'white',
        position: 'relative',
        cursor: (showGreenHighlight || showRedHighlight || showYellowHighlight) ? 'pointer' : 'default',
        opacity: isDimmed ? 0.25 : 1,
        filter: isDimmed ? 'grayscale(60%)' : 'none',
        pointerEvents: isDimmed ? 'none' : 'auto',
        transition: 'opacity 0.4s ease, filter 0.4s ease',
        ...(showGreenHighlight && {
          animation: 'pulse-green 5s ease-in-out 1',
          border: '2px solid rgba(76, 175, 80, 0.8)',
          boxShadow: '0 0 15px 5px rgba(76, 175, 80, 0.4)',
          '@keyframes pulse-green': {
            '0%, 100%': {
              boxShadow: '0 0 15px 5px rgba(76, 175, 80, 0.4)',
              transform: 'scale(1)',
            },
            '50%': {
              boxShadow: '0 0 20px 10px rgba(76, 175, 80, 0.6)',
              transform: 'scale(1.02)',
            },
          },
        }),
        ...(showRedHighlight && {
          animation: 'pulse-red 5s ease-in-out 1',
          border: '2px solid rgba(244, 67, 54, 0.8)',
          boxShadow: '0 0 15px 5px rgba(244, 67, 54, 0.4)',
          '@keyframes pulse-red': {
            '0%, 100%': {
              boxShadow: '0 0 15px 5px rgba(244, 67, 54, 0.4)',
              transform: 'scale(1)',
            },
            '50%': {
              boxShadow: '0 0 20px 10px rgba(244, 67, 54, 0.6)',
              transform: 'scale(1.02)',
            },
          },
        }),
        ...(showYellowHighlight && {
          animation: 'pulse-yellow 5s ease-in-out 1',
          border: '2px solid rgba(255, 193, 7, 0.8)',
          boxShadow: '0 0 15px 5px rgba(255, 193, 7, 0.4)',
          '@keyframes pulse-yellow': {
            '0%, 100%': {
              boxShadow: '0 0 15px 5px rgba(255, 193, 7, 0.4)',
              transform: 'scale(1)',
            },
            '50%': {
              boxShadow: '0 0 20px 10px rgba(255, 193, 7, 0.6)',
              transform: 'scale(1.02)',
            },
          },
        }),
      }}
    >
      <CardContent sx={{ p: { xs: 1, md: 2 } }}>

        {/* Mobile Layout */}
        <Box display={{ xs: 'block', md: 'none' }}>
          {/* Top Row: Preview + Title/Address */}
          <Box display="flex" gap={1} mb={1}>
            {/* Preview */}
            <Box
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onView) onView(certificate.id);
              }}
              sx={{
                width: 80,
                height: 100,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
                cursor: onView ? 'pointer' : 'default',
                '&:hover': onView ? {
                  opacity: 0.8,
                  borderColor: 'primary.main'
                } : {}
              }}
            >
              <img
                src={`${config.apiUrl}/api/certificate-preview/${certificate.id}?t=${encodeURIComponent(certificate.pdf_filename || certificate.created_at || Date.now().toString())}`}
                alt={`${certificate.certificate_type} preview`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLElement).parentElement;
                  if (parent && !parent.querySelector('.pdf-fallback')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'pdf-fallback';
                    fallback.style.cssText = `
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      height: 100%;
                      background-color: #f5f5f5;
                      color: #666;
                    `;
                    fallback.innerHTML = `
                      <div style="text-align: center;">
                        <div style="font-size: 24px; margin-bottom: 4px;">📄</div>
                        <div style="font-size: 10px;">PDF</div>
                      </div>
                    `;
                    parent.appendChild(fallback);
                  }
                }}
              />
            </Box>

            {/* Title + Address */}
            <Box flex={1}>
              <Typography 
                variant="h6" 
                component="h3" 
                sx={{ fontSize: '0.9rem', lineHeight: 1, mb: 0.25 }}
                title={certificate.original_filename || 'No filename available'}
              >
                {certificate.certificate_type}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                📍 {certificate.street_address || certificate.normalized_address || certificate.property_address}
              </Typography>
            </Box>
          </Box>

          {/* Badge + Icons Row - Mobile */}
          <Box display="flex" alignItems="center" gap={0.5} mb={1} flexWrap="nowrap">
            {/* Category Badge */}
            {certificate.certificate_type_category && certificate.certificate_type_category !== 'Other' && (
              <Chip 
                label={certificate.certificate_type_category.replace(' Certificate', '')}
                size="small"
                sx={{
                  height: '18px',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  backgroundColor: getCategoryColor(certificate.certificate_type_category),
                  color: 'white',
                  '& .MuiChip-label': { px: 0.75, py: 0 }
                }}
              />
            )}
            
            {/* All Action Icons */}
            {onEdit && (
              <Tooltip title="Edit certificate details">
                <IconButton 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit(certificate);
                  }} 
                  size="small"
                  sx={{ 
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' }
                  }}
                >
                  <EditIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            )}
            
            {!hideActions && (<>
            <Tooltip title="View version history">
              <IconButton 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOpenVersionHistory();
                }} 
                size="small"
                sx={{ 
                  backgroundColor: 'info.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'info.dark' }
                }}
              >
                <HistoryIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={
              certificate.last_email_sent_at 
                ? `Email sent ${new Date(certificate.last_email_sent_at).toLocaleDateString()} to ${certificate.last_email_sent_to}`
                : "Send inspection request email"
            }>
              <span>
                {certificate.last_email_sent_at ? (
                  <Button
                    onClick={() => setEmailDialogOpen(true)}
                    size="small"
                    startIcon={<EmailIcon sx={{ fontSize: 12 }} />}
                    sx={{ 
                      backgroundColor: 'success.main',
                      color: 'white',
                      minWidth: 'auto',
                      px: 0.5,
                      py: 0.3,
                      fontSize: '0.6rem',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: 'success.dark' },
                      '&.Mui-disabled': {
                        backgroundColor: 'success.main',
                        color: 'white',
                        opacity: 0.7
                      },
                      borderRadius: 1
                    }}
                  >
                    SENT
                  </Button>
                ) : (
                  <IconButton 
                    onClick={() => setEmailDialogOpen(true)} 
                    size="small"
                    sx={{ 
                      backgroundColor: 'white',
                      color: 'info.main',
                      border: '1px solid',
                      borderColor: 'info.main',
                      '&:hover': { backgroundColor: 'info.50' }
                    }}
                  >
                    <EmailIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}
              </span>
            </Tooltip>
            <Tooltip title={hasActiveReminders ? "Edit reminder" : "Set reminder"}>
              <span>
                {hasActiveReminders ? (
                  <Button
                    onClick={handleReminderClick}
                    disabled={isCreatingReminder}
                    size="small"
                    startIcon={
                      isCreatingReminder ? (
                        <CircularProgress size={12} sx={{ color: 'white' }} />
                      ) : (
                        <Notifications sx={{ fontSize: 12 }} />
                      )
                    }
                    sx={{ 
                      backgroundColor: 'success.main',
                      color: 'white',
                      minWidth: 'auto',
                      px: 0.5,
                      py: 0.3,
                      fontSize: '0.6rem',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: 'success.dark' },
                      '&:disabled': {
                        backgroundColor: 'success.main',
                        color: 'white',
                        opacity: 0.8
                      }
                    }}
                  >
                    Set
                  </Button>
                ) : (
                  <IconButton
                    onClick={handleReminderClick}
                    disabled={isCreatingReminder}
                    size="small"
                    sx={{ 
                      backgroundColor: 'white',
                      color: 'primary.main',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' },
                      '&:disabled': {
                        backgroundColor: 'white',
                        color: 'grey.400',
                      }
                    }}
                  >
                    {isCreatingReminder ? (
                      <CircularProgress size={12} sx={{ color: 'primary.main' }} />
                    ) : (
                      <Notifications sx={{ fontSize: 12 }} />
                    )}
                  </IconButton>
                )}
              </span>
            </Tooltip>
            </>)}
            
            {onDelete && (
              <IconButton 
                onClick={() => setDeleteConfirmOpen(true)} 
                size="small"
                sx={{ 
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'error.dark' }
                }}
              >
                <DeleteIcon sx={{ fontSize: 12 }} />
              </IconButton>
            )}
          </Box>

          {/* Dates Section */}
          <Box display="flex" flexDirection="column" gap={1}>
            {/* Issue Date */}
            {certificate.issue_date && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                  Issue Date
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                  {formatDate(certificate.issue_date)}
                </Typography>
              </Box>
            )}

            {/* Expiry Date with Status */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                Expiry Date
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                  {formatDate(certificate.expiry_date)}
                </Typography>
                <Chip
                  label={expiryStatus.label}
                  color={expiryStatus.color as any}
                  size="small"
                  icon={expiryStatus.severity === 'error' ? <Error /> : 
                        expiryStatus.severity === 'warning' ? <Warning /> : 
                        <CheckCircle />}
                  sx={{ fontSize: '0.65rem' }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Desktop Layout */}
        <Box display={{ xs: 'none', md: 'flex' }} flexDirection="column" gap={1.5}>

          {/* Row 1: preview thumbnail + text content */}
          <Box display="flex" gap={2} alignItems="stretch">

            {/* Thumbnail — fixed size, never grows */}
            <Box
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (onView) onView(certificate.id); }}
              sx={{
                width: 120, height: 152, flexShrink: 0,
                border: '1px solid', borderColor: 'divider', borderRadius: 1,
                overflow: 'hidden', cursor: onView ? 'pointer' : 'default',
                '&:hover': onView ? { opacity: 0.8, borderColor: 'primary.main' } : {},
              }}
            >
              <img
                src={`${config.apiUrl}/api/certificate-preview/${certificate.id}?t=${encodeURIComponent(certificate.pdf_filename || certificate.created_at || Date.now().toString())}`}
                alt={`${certificate.certificate_type} preview`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLElement).parentElement;
                  if (parent && !parent.querySelector('.pdf-fallback')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'pdf-fallback';
                    fallback.style.cssText = 'display:flex;align-items:center;justify-content:center;height:100%;background:#f5f5f5;color:#666;';
                    fallback.innerHTML = '<div style="text-align:center"><div style="font-size:24px;margin-bottom:4px">📄</div><div style="font-size:10px">PDF</div></div>';
                    parent.appendChild(fallback);
                  }
                }}
              />
            </Box>

            {/* Text content — can grow, but never bleeds into thumbnail */}
            <Box flex={1} minWidth={0} display="flex" flexDirection="column">
              <Typography
                variant="h6"
                component="h3"
                sx={{ fontSize: '1.05rem', lineHeight: 1.3, mb: 0.5, wordBreak: 'break-word' }}
                title={certificate.original_filename || 'No filename available'}
              >
                {certificate.certificate_type}
              </Typography>

              {certificate.original_filename && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {certificate.original_filename}
                </Typography>
              )}

              {/* Dates row — pinned to bottom of the content column */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', mt: 'auto' }}>
                {certificate.issue_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}>Issued</Typography>
                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.82rem' }}>{formatDate(certificate.issue_date)}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}>Expires</Typography>
                  <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.82rem' }}>{formatDate(certificate.expiry_date)}</Typography>
                </Box>
                <Chip
                  label={expiryStatus.label}
                  color={expiryStatus.color as any}
                  size="small"
                  icon={expiryStatus.severity === 'error' ? <Error /> : expiryStatus.severity === 'warning' ? <Warning /> : <CheckCircle />}
                  sx={{ fontSize: '0.72rem', mt: 0.5 }}
                />
              </Box>
            </Box>
          </Box>

          {/* Row 2: badge + action icons — always anchored at the bottom, full width */}
          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
            {certificate.certificate_type_category && certificate.certificate_type_category !== 'Other' && (
              <Chip
                label={certificate.certificate_type_category.replace(' Certificate', '')}
                size="small"
                sx={{ height: '22px', fontSize: '0.7rem', fontWeight: 600, backgroundColor: getCategoryColor(certificate.certificate_type_category), color: 'white', '& .MuiChip-label': { px: 1, py: 0 } }}
              />
            )}

            {onEdit && (
              <Tooltip title="Edit certificate details">
                <IconButton onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(certificate); }} size="small" sx={{ backgroundColor: 'primary.main', color: 'white', '&:hover': { backgroundColor: 'primary.dark' } }}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}

            {!hideActions && (<>
            <Tooltip title="View version history">
              <IconButton onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenVersionHistory(); }} size="small" sx={{ backgroundColor: 'info.main', color: 'white', '&:hover': { backgroundColor: 'info.dark' } }}>
                <HistoryIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={certificate.last_email_sent_at ? `Email sent ${new Date(certificate.last_email_sent_at).toLocaleDateString()} to ${certificate.last_email_sent_to}` : "Send inspection request email"}>
              <span>
                {certificate.last_email_sent_at ? (
                  <Button onClick={() => setEmailDialogOpen(true)} size="small" startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
                    sx={{ backgroundColor: 'success.main', color: 'white', minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem', textTransform: 'none', borderRadius: 1, '&:hover': { backgroundColor: 'success.dark' } }}>
                    SENT
                  </Button>
                ) : (
                  <IconButton onClick={() => setEmailDialogOpen(true)} size="small" sx={{ backgroundColor: 'white', color: 'info.main', border: '1px solid', borderColor: 'info.main', '&:hover': { backgroundColor: 'info.50' } }}>
                    <EmailIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </span>
            </Tooltip>

            <Tooltip title={hasActiveReminders ? "Edit reminder" : "Set reminder"}>
              <span>
                {hasActiveReminders ? (
                  <Button onClick={handleReminderClick} disabled={isCreatingReminder} size="small"
                    startIcon={isCreatingReminder ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <Notifications sx={{ fontSize: 16 }} />}
                    sx={{ backgroundColor: 'success.main', color: 'white', minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem', textTransform: 'none', '&:hover': { backgroundColor: 'success.dark' }, '&:disabled': { backgroundColor: 'success.main', color: 'white', opacity: 0.8 } }}>
                    Set
                  </Button>
                ) : (
                  <IconButton onClick={handleReminderClick} disabled={isCreatingReminder} size="small"
                    sx={{ backgroundColor: 'white', color: 'primary.main', border: '1px solid', borderColor: 'primary.main', '&:hover': { backgroundColor: 'primary.50' }, '&:disabled': { backgroundColor: 'white', color: 'grey.400' } }}>
                    {isCreatingReminder ? <CircularProgress size={14} sx={{ color: 'primary.main' }} /> : <Notifications sx={{ fontSize: 16 }} />}
                  </IconButton>
                )}
              </span>
            </Tooltip>
            </>)}

            {onDelete && (
              <IconButton onClick={() => setDeleteConfirmOpen(true)} size="small" sx={{ backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' } }}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </CardContent>

      {/* Reminder Options Menu */}
      <Menu
        anchorEl={reminderMenuAnchor}
        open={Boolean(reminderMenuAnchor)}
        onClose={() => setReminderMenuAnchor(null)}
      >
        {reminderOptions.map((option) => (
          <MenuItem
            key={option.days}
            onClick={() => createReminder(option.days)}
            disabled={reminderStatus?.active_reminders?.some(r => r.reminder_days_before === option.days)}
          >
            <ListItemIcon>
              {option.icon}
            </ListItemIcon>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Reminder Edit Dialog */}
      <ReminderEditDialog
        open={reminderDialogOpen}
        onClose={handleReminderDialogClose}
        certificate={certificate}
        onSave={handleReminderSave}
        onDelete={handleDeleteReminder}
        existingReminder={reminderStatus?.active_reminders?.[0] ? (() => {
          const reminder = reminderStatus.active_reminders[0];
          const reminderDateTime = new Date(reminder.reminder_date);
          const hours = reminderDateTime.getHours().toString().padStart(2, '0');
          const minutes = reminderDateTime.getMinutes().toString().padStart(2, '0');
          return {
            text: `${certificate.certificate_type} certificate expires on ${certificate.expiry_date}`,
            daysBeforeExpiry: reminder.reminder_days_before || 7,
            reminderId: reminder.id,
            reminderTime: `${hours}:${minutes}`,
            reminderDate: reminderDateTime.toISOString().split('T')[0] // actual stored date from DB
          };
        })() : undefined}
      />

      {/* Email Dialog */}
      <EmailDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        certificate={certificate}
        onEmailSent={onReminderSuccess}
        onError={onReminderError}
      />

      {/* Version History Dialog */}
      <Dialog 
        open={versionDialogOpen} 
        onClose={() => setVersionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Version History - {certificate.certificate_type}
        </DialogTitle>
        <DialogContent>
          {loadingVersions ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : versionError ? (
            <Alert severity="error">{versionError}</Alert>
          ) : versions.length === 0 ? (
            <Alert severity="info">No previous versions found</Alert>
          ) : (
            <Box>
              {versions.map((version, index) => (
                <Card key={version.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Typography variant="subtitle2" gutterBottom>
                          Version {version.version_number} {index === 0 && <Chip label="Previous" size="small" color="primary" sx={{ ml: 1 }} />}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          {version.property_address}
                        </Typography>
                        <Box display="flex" gap={2} mt={1}>
                          <Typography variant="caption">
                            <strong>Issue:</strong> {version.issue_date || 'N/A'}
                          </Typography>
                          <Typography variant="caption">
                            <strong>Expiry:</strong> {version.expiry_date || 'N/A'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                          Replaced by: {version.replaced_by_provider || 'Manual'} • {new Date(version.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={() => handleRevertToVersion(version.id)}
                      >
                        Revert
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Certificate?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this certificate? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            <strong>{certificate.certificate_type}</strong><br />
            {certificate.property_address}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              if (onDelete) {
                onDelete(certificate.id);
              }
            }} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default EnhancedCertificateCard;